import fetchPonyfill from 'fetch-ponyfill';
import {
    fromPromise,
    ApolloLink,
    Observable,
    NextLink,
    Operation,
    FetchResult,
} from '@apollo/client';
import * as Sentry from '@sentry/browser';

import { ErrorLink } from '@apollo/client/link/error';
import loadConfiguration from 'ggApp/utils/configuration';
import {
    saveAuthCredentials,
    getAccessToken,
    getRefreshToken,
    forgetAuthCredentials,
} from 'ggApp/modules/auth/utils';
import { shutdownIntercom } from 'ggApp/modules/intercom/service';
import { isAppUser } from 'ggApp/routes/_wrap/MobileWrapper/utils';

const { fetch } = fetchPonyfill();

const { APP_CONFIG } = loadConfiguration();
const { apiUrl } = APP_CONFIG;

type AuthCredentials = {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
};

type ObservableAccessTokenRefresmentParameters = {
    operation: Operation;
    forward: NextLink;
};

let refreshTokenPromise: Promise<AuthCredentials> | null = null;

function refreshAccessToken(cookiesManager: any, headers: any): Promise<AuthCredentials> {
    return new Promise((resolve, reject): void => {
        const refreshToken = getRefreshToken(cookiesManager);

        if (!refreshToken) {
            return reject(new Error("Refresh token doesn't exist"));
        }

        const config = {
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
        };

        fetch(`${apiUrl}/oauth/token`, {
            method: 'POST',
            mode: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(config),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(response.statusText);
                }

                return response.json();
            })
            .then((data) => {
                const {
                    access_token: accessToken,
                    refresh_token: newRefreshToken,
                    expires_in: expiresIn,
                } = data;

                saveAuthCredentials(accessToken, newRefreshToken, expiresIn, cookiesManager);

                return {
                    accessToken,
                    refreshToken: newRefreshToken,
                    expiresIn,
                };
            })
            .then(resolve)
            .catch((err) => {
                if (!__IS_SERVER__) {
                    shutdownIntercom();
                }

                forgetAuthCredentials(cookiesManager);

                if (isAppUser(headers)) {
                    Sentry.captureException(`App user force logout in refreshAccessToken: ${err}`);
                }

                reject(err);
            });

        return undefined;
    });
}

function handleObservableAccessTokenRefreshment({
    operation,
    forward,
}: ObservableAccessTokenRefresmentParameters): Observable<
    FetchResult<Record<string, any>, Record<string, any>, Record<string, any>>
> {
    const { headers, cookiesManager } = operation.getContext();

    if (!refreshTokenPromise) {
        refreshTokenPromise = refreshAccessToken(cookiesManager, headers);
    }

    const refreshTokenObservable = fromPromise(
        refreshTokenPromise.finally(() => {
            refreshTokenPromise = null;
        }),
    ).flatMap((result) => {
        const { accessToken: refreshedAccessToken } = result;

        operation.setContext({
            headers: {
                ...headers,
                authorization: `Bearer ${refreshedAccessToken}`,
            },
        });

        return forward(operation);
    });

    return refreshTokenObservable;
}

const ErrorHandlingMiddleware = new ErrorLink(({ graphQLErrors, operation, forward }) => {
    const { cookiesManager, accessToken } = operation.getContext();

    const refreshToken = getRefreshToken(cookiesManager);

    const hasUnauthenticatedError = graphQLErrors?.some(
        (error) => error.extensions?.code === 'UNAUTHENTICATED',
    );

    if (!accessToken && hasUnauthenticatedError && refreshToken) {
        return handleObservableAccessTokenRefreshment({ operation, forward });
    }

    return undefined;
});

const BeforeQueryMiddleware = new ApolloLink((operation, forward) => {
    const { cookiesManager, accessToken } = operation.getContext();

    const cookiesAccessToken = getAccessToken(cookiesManager);
    const refreshToken = getRefreshToken(cookiesManager);

    if (refreshToken && !accessToken && !cookiesAccessToken) {
        return handleObservableAccessTokenRefreshment({ operation, forward });
    }

    return forward(operation);
});

const RefreshTokenMiddleware = ApolloLink.from([BeforeQueryMiddleware, ErrorHandlingMiddleware]);

export default RefreshTokenMiddleware;
